/**
 * Zone topology for Virtuaroo. Each zone maps to one Durable Object instance.
 * Bounds are in the local meters-from-Arch coordinate system used by the
 * terrain pipeline (see scripts/build-heightmap.ts).
 *
 * See SPEC.md §4 (Zoning) for the rationale.
 */

export interface ZoneDef {
  id: string;
  label: string;
  /** Center XZ in local meters. Y is always ground. */
  center: [number, number];
  /** Soft cylindrical radius in meters that defines the zone. */
  radius: number;
  /** Adjacent zones a player may hand off to. */
  neighbors: string[];
  /** Zones the spawn picker shows as a top drop spot. */
  spawnable: boolean;
  /** UI hint shown in the spawn picker. */
  vibe?: string;
  /** Offset from center where the player actually spawns (in audience, not on stage). */
  spawnOffset?: [number, number];
  /** Yaw the player faces on spawn (radians). 0 = look down -Z. */
  spawnYaw?: number;
}

/**
 * Coordinates are placeholders calibrated to the rough Bonnaroo footprint.
 * Phase 1 will replace these with values measured from the USGS DEM tile.
 */
export const ZONES: Record<string, ZoneDef> = {
  arch: {
    id: 'arch',
    label: 'The Arch',
    center: [0, 0],
    radius: 60,
    neighbors: ['centeroo-core'],
    spawnable: true,
    vibe: 'Where every Bonnaroovian first walks in.',
    // Stand 25m back from the arch, looking AT it (toward -Z = north into Centeroo).
    spawnOffset: [0, 35],
    spawnYaw: Math.PI
  },
  'centeroo-core': {
    id: 'centeroo-core',
    label: 'Centeroo Core',
    center: [0, 220],
    radius: 120,
    neighbors: ['arch', 'what-stage', 'which-stage', 'this-that-tents', 'other-stage'],
    spawnable: true,
    vibe: 'Fountain, vendors, and the heart of the festival.',
    spawnOffset: [25, 25],
    spawnYaw: -Math.PI / 2
  },
  'what-stage': {
    id: 'what-stage',
    label: 'What Stage',
    center: [-300, 380],
    radius: 220,
    neighbors: ['centeroo-core', 'which-stage'],
    spawnable: true,
    vibe: 'The mainstage. 80,000-capacity field.',
    // Audience side is +x of stage (stage rotated 90deg so it faces +x).
    spawnOffset: [60, 0],
    spawnYaw: -Math.PI / 2
  },
  'which-stage': {
    id: 'which-stage',
    label: 'Which Stage',
    center: [120, 360],
    radius: 180,
    neighbors: ['centeroo-core', 'what-stage', 'this-that-tents'],
    spawnable: true,
    vibe: 'Breakthrough acts and big risers.',
    // Stage rotated 200deg, audience is opposite the stage front.
    spawnOffset: [-30, 50],
    spawnYaw: 2.5
  },
  'this-that-tents': {
    id: 'this-that-tents',
    label: 'This Tent + That Tent',
    center: [260, 220],
    radius: 140,
    neighbors: ['centeroo-core', 'which-stage', 'other-stage'],
    spawnable: false,
    vibe: 'Eclectic acts and Superjams.',
    spawnOffset: [0, 30],
    spawnYaw: Math.PI
  },
  'other-stage': {
    id: 'other-stage',
    label: 'The Other Stage',
    center: [320, 60],
    radius: 160,
    neighbors: ['centeroo-core', 'this-that-tents', 'outeroo-pods'],
    spawnable: true,
    vibe: 'EDM and elaborate light shows after dark.',
    // Stage rotated 180deg → audience is on -z side.
    spawnOffset: [0, -50],
    spawnYaw: 0
  },
  woods: {
    id: 'woods',
    label: 'Where in the Woods',
    center: [-380, -180],
    radius: 200,
    neighbors: ['outeroo-pods'],
    spawnable: true,
    vibe: 'Late-night woods stage. Headphones recommended.',
    // Stage rotated 45deg.
    spawnOffset: [25, 25],
    spawnYaw: -Math.PI * 0.75
  },
  'outeroo-pods': {
    id: 'outeroo-pods',
    label: 'Outeroo + Plaza Barns',
    center: [-100, -380],
    radius: 320,
    neighbors: ['arch', 'woods', 'other-stage', 'ferris'],
    spawnable: false,
    vibe: 'Camping pods and afternoon yoga.'
  },
  ferris: {
    id: 'ferris',
    label: 'Ferris Wheel',
    center: [200, -300],
    radius: 80,
    neighbors: ['outeroo-pods'],
    spawnable: true,
    vibe: 'Best view on the farm.',
    spawnOffset: [25, 25],
    spawnYaw: -Math.PI * 0.75
  }
};

export const SPAWN_POINTS = Object.values(ZONES).filter((z) => z.spawnable);

/** Find which zone a world position belongs to (closest center within radius). */
export function zoneAt(x: number, z: number): ZoneDef | null {
  let best: ZoneDef | null = null;
  let bestDist = Infinity;
  for (const zone of Object.values(ZONES)) {
    const dx = x - zone.center[0];
    const dz = z - zone.center[1];
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d < zone.radius && d < bestDist) {
      best = zone;
      bestDist = d;
    }
  }
  return best;
}
